import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../../App';
import { Building2, Users, UserPlus, XCircle, Shield } from 'lucide-react';
import { toast } from 'sonner';

const PartnerStaff = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStaff = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/partner/staff`);
      setStaff(res.data);
    } catch (err) {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const handleRemove = async (member) => {
    if (member.email === user?.email) {
      toast.error("You can't remove yourself");
      return;
    }
    if (!window.confirm(`Remove ${member.name} (${member.email}) from the organization?\n\nThey will become a regular user and lose access to the partner portal.`)) return;
    try {
      await axios.delete(`${API}/partner/staff/${encodeURIComponent(member.email)}`);
      toast.success(`${member.name} removed from organization`);
      fetchStaff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove staff');
    }
  };

  const isAdmin = user?.role === 'partner_admin';

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="text-lg">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-[#FBF4EA]" data-testid="partner-staff-page">
      <nav className="bg-white border-b-2 border-[#E9DDCF]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Building2 className="w-6 h-6 text-[#8B2E3C]" />
              <div className="font-bold text-lg text-[#222]">{user?.partner_org_name || 'Partner Portal'}</div>
            </div>
            <div className="flex items-center gap-4 text-sm overflow-x-auto">
              <Link to="/partner/dashboard" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Dashboard</Link>
              <Link to="/partner/opportunities" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Opportunities</Link>
              <Link to="/partner/community" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Community Board</Link>
              <Link to="/partner/participants" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Participants</Link>
              <Link to="/partner/invites" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Invites</Link>
              <Link to="/partner/staff" className="text-[#8B2E3C] font-bold whitespace-nowrap">Staff</Link>
              <Link to="/partner/time-entries" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Time Entries</Link>
              <Link to="/partner/reports" className="text-[#222] hover:text-[#8B2E3C] font-medium whitespace-nowrap">Reports</Link>
              <button onClick={logout} className="text-[#8B2E3C] hover:underline whitespace-nowrap">Logout</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#222]">Staff Members</h1>
            <p className="text-[#666] mt-1">Manage your organization's team</p>
          </div>
          {isAdmin && <button onClick={() => navigate('/partner/invites')} className="btn-primary flex items-center gap-2" data-testid="invite-staff-btn">
            <UserPlus className="w-4 h-4" /> Invite New Staff
          </button>}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
          <div className="card text-center">
            <div className="text-2xl font-bold text-[#8B2E3C]">{staff.length}</div>
            <div className="text-xs text-[#666]">Total Staff</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-[#C9A227]">{staff.filter(s => s.role === 'partner_admin').length}</div>
            <div className="text-xs text-[#666]">Admins</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-[#222]">{staff.filter(s => s.role === 'partner_staff').length}</div>
            <div className="text-xs text-[#666]">Staff</div>
          </div>
        </div>

        {/* Staff List */}
        {staff.length === 0 ? (
          <div className="card text-center py-12 text-[#666]">
            <Users className="w-12 h-12 mx-auto mb-3 text-[#ccc]" />
            <p className="mb-4">No staff members yet.</p>
            {isAdmin && <button onClick={() => navigate('/partner/invites')} className="btn-primary">Send First Invite</button>}
          </div>
        ) : (
          <div className="space-y-3">
            {staff.map(member => {
              const isCurrentUser = member.email === user?.email;
              return (
                <div key={member.email} className="card" data-testid={`staff-row-${member.email}`}>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#8B2E3C] text-white flex items-center justify-center font-bold text-sm shrink-0">
                        {member.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-[#222]">{member.name}</span>
                          {isCurrentUser && <span className="text-xs bg-[#FBF4EA] px-2 py-0.5 rounded text-[#8B2E3C]">You</span>}
                        </div>
                        <div className="text-xs text-[#666]">{member.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${member.role === 'partner_admin' ? 'bg-[#8B2E3C]/10 text-[#8B2E3C]' : 'bg-gray-100 text-gray-600'}`}>
                        <Shield className="w-3 h-3" />
                        {member.role === 'partner_admin' ? 'Admin' : 'Staff'}
                      </span>
                      {isAdmin && !isCurrentUser && (
                        <button
                          onClick={() => handleRemove(member)}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Remove from organization"
                          data-testid={`remove-staff-${member.email}`}
                        >
                          <XCircle className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerStaff;
